import {
  EdgeEndpointConfiguration,
  getEndpointConfiguration,
  request,
} from ".";
import { EdgeSystemStatus, EdgeSystemType, SystemState } from '../types';

export const sendRestartRequest = async (callbackOrigin?: string) =>
  await sendSystemActionRequest("restart", callbackOrigin);
export const sendAnonymousRestartRequest = async (callbackOrigin?: string) =>
  await sendSystemActionRequest("anonymousRestart", callbackOrigin);
export const sendResetRequest = async (callbackOrigin?: string) =>
  await sendSystemActionRequest("reset", callbackOrigin);
export const sendAnonymousResetRequest = async (callbackOrigin?: string) =>
  await sendSystemActionRequest("anonymousReset", callbackOrigin);
export const sendUpdateRequest = async (callbackOrigin?: string) =>
  await sendSystemActionRequest("update", callbackOrigin);
export const sendCanUpdateRequest = async (callbackOrigin?: string) =>
  await sendSystemActionRequest("canUpdate", callbackOrigin);

const sendSystemActionRequest = async (
  verb: string,
  callbackOrigin?: string
) => {
  const endpoints = getEndpointConfiguration();
  if (endpoints.isCloud) {
    console.warn("Attempted to invoke a system action in the cloud");
    return;
  }

  const edgeEndpoints = endpoints as EdgeEndpointConfiguration;

  const requestUrl = `${edgeEndpoints.api}/api/v1/System/${verb}`;
  const callbackUrlOrigin = callbackOrigin ?? window.location.origin;

  const callbackVerb = verb.replace("anonymous", "").toLowerCase();
  const requestParams = {
    body: { callbackUrl: `${callbackUrlOrigin}/api/${callbackVerb}` },
  };

  try {
    const responseData = await request.post<boolean>(requestUrl, requestParams);
    return responseData ?? false;
  } catch (error) {
    console.error(
      "Error occurred while invoking system action request: ",
      error,
      requestUrl,
      requestParams
    );
    return false;
  }
};

export const getDefaultState = () => ({
    id: '',
    systemType: EdgeSystemType.None,
    status: EdgeSystemStatus.Initial,
    lastSuccessfulTimeUtc: null
});

export const defaultSystemState: SystemState = {
  [EdgeSystemType.None]: getDefaultState(),
  [EdgeSystemType.DataSync]: getDefaultState(),
  [EdgeSystemType.LogSync]: getDefaultState(),
  [EdgeSystemType.CloudConnection]: getDefaultState(),
  [EdgeSystemType.CloudAuth]: getDefaultState(),
};

export const getSystemState = async (apiHost: string) => {
  const getStateEndpointUrl = `${apiHost}/api/v1/System/State`;
  let systemStateData = defaultSystemState;

  try {
    systemStateData = await request.get<SystemState>(getStateEndpointUrl) ?? defaultSystemState;

    // Set this so we can tell if the initial state fetch was a success
    systemStateData[EdgeSystemType.None] ??= getDefaultState();
    systemStateData[EdgeSystemType.None].status = EdgeSystemStatus.Success;
  } catch (e) {
    console.error(`Error sending network request to '${getStateEndpointUrl}'`, e)
  }

  let newState = { ...defaultSystemState };
  for (let [key, val] of Object.entries(systemStateData)) {
    const systemType = EdgeSystemType[key as keyof typeof EdgeSystemType];
    newState[systemType] = val;
  }

  return newState;
};

export const getInitialSyncStatus = async (apiHost: string) => {
  const getSyncStatusEndpointUrl = `${apiHost}/api/v1/Sync/status`;
  let initialSyncStatus = EdgeSystemStatus.Initial;

  try {
    initialSyncStatus = await request.get<EdgeSystemStatus>(getSyncStatusEndpointUrl) ?? EdgeSystemStatus.Initial;
  } catch (e) {
    console.error(`Error sending network request to '${getSyncStatusEndpointUrl}'`, e)
  }
  
  return initialSyncStatus;
};
